import React from "react";
import { Link } from "react-router-dom";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";
import { useContent } from "@/components/PublicLayout";
import { PRODUCT_LIST } from "@/data/products";
import { Check, ShieldCheck, Zap, MapPin, HeartHandshake, Phone, ArrowRight } from "lucide-react";

const TRUST = ["Fast & Free Quotes", "Multiple Insurance Options", "Licensed in PA, NJ, NY & FL", "Personal Service", "No Obligation"];
const WHY = [
  { icon: Zap, t: "Fast Quotes", d: "Start online in minutes and hear back the same business day." },
  { icon: MapPin, t: "Licensed in 4 States", d: "Serving clients across Pennsylvania, New Jersey, New York, and Florida." },
  { icon: ShieldCheck, t: "Multiple Options", d: "As an independent agency, we shop coverage to fit your needs and budget." },
  { icon: HeartHandshake, t: "Personal Service", d: "Real people who take the time to explain your options — no pressure." },
];

export default function Home() {
  const content = useContent();
  const phone = content?.phone || "(570) 661-3771";
  const testimonials = content?.testimonials || [];
  const carriers = content?.carriers || ["Progressive", "Geico", "National General", "Bristol West"];

  return (
    <div>
      <Seo
        title="Fast, Free Insurance Quotes in PA, NJ, NY & FL"
        description="Looking for affordable or cheaper insurance in the Poconos? Keystone Shield Insurance is a local independent agency in Mount Pocono, PA. Compare car, home, bundle, business & life insurance quotes fast and free. Licensed in PA, NJ, NY & FL."
        path="/"
        keywords="cheap car insurance, cheaper insurance, affordable insurance quotes, insurance Mount Pocono PA, insurance Poconos, auto insurance near me, home insurance quote, compare insurance quotes PA NJ NY FL"
      />
      {/* HERO */}
      <section className="relative bg-[#0B1E36] text-white overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-5 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center relative">
          <div className="animate-rise">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] font-bold mb-5">Independent · Licensed in PA · NJ · NY · FL</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight">
              BETTER COVERAGE.<br /><span className="text-[#C5A059]">BETTER RATES.</span><br />ONE LOCAL AGENCY.
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-lg">
              Let Keystone Shield Insurance shop for coverage that fits your needs and budget. Get a fast, free insurance quote today.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/quote" className="bg-[#C5A059] text-[#0B1E36] font-bold px-7 h-14 rounded-sm flex items-center justify-center hover:bg-white transition-colors" data-testid="hero-get-quote">GET MY FREE QUOTE</Link>
              <a href={`tel:${phone}`} className="border border-white/30 px-7 h-14 rounded-sm flex items-center justify-center gap-2 font-semibold hover:bg-white/10 transition-colors" data-testid="hero-call"><Phone size={18} /> CALL FOR A QUOTE</a>
            </div>
            <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-2">
              {TRUST.map((t) => <li key={t} className="flex items-center gap-2 text-sm text-slate-200"><Check size={16} className="text-[#C5A059]" /> {t}</li>)}
            </ul>
          </div>
          <div className="animate-rise" style={{ animationDelay: "0.1s" }}>
            <QuoteForm />
          </div>
        </div>
      </section>

      {/* CARRIERS */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 py-8">
          <p className="text-center text-xs uppercase tracking-[0.3em] text-slate-500 font-bold mb-5">As an independent agency, we shop top carriers for you</p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {carriers.map((c) => (
              <span key={c} className="font-display text-xl sm:text-2xl font-bold text-[#0B1E36]/70" data-testid={`carrier-${c.replace(/\s/g,'-').toLowerCase()}`}>{c}</span>
            ))}
            <span className="text-sm text-slate-500">+ many commercial carriers</span>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="max-w-7xl mx-auto px-5 py-16 lg:py-24">
        <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] font-bold">Coverage Options</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2 mb-10">Insurance built around your life.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PRODUCT_LIST.map((p) => (
            <Link key={p.slug} to={`/${p.slug}`} data-testid={`product-card-${p.key}`}
              className="group bg-white border border-slate-200 rounded-sm p-6 hover:-translate-y-1 hover:shadow-lg transition-transform transition-shadow duration-200">
              <h3 className="font-display text-xl font-bold">{p.type}</h3>
              <p className="text-sm text-slate-600 mt-2 line-clamp-2">{p.intro}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#C5A059]">Get a Free Quote <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section className="bg-[#0B1E36] text-white">
        <div className="max-w-7xl mx-auto px-5 py-16 lg:py-24">
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-10">Why customers choose us</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w) => (
              <div key={w.t} className="border border-white/10 rounded-sm p-6">
                <w.icon className="text-[#C5A059] mb-4" size={28} />
                <h3 className="font-display text-lg font-bold">{w.t}</h3>
                <p className="text-sm text-slate-300 mt-2">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 py-16 lg:py-24">
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-10">What our customers say</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <figure key={i} className="bg-white border border-slate-200 rounded-sm p-6" data-testid={`testimonial-${i}`}>
                <blockquote className="text-slate-700 italic">“{t.text}”</blockquote>
                <figcaption className="mt-4 font-semibold text-sm">{t.name}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* SERVICE AREAS */}
      <section className="max-w-7xl mx-auto px-5 py-14 border-t border-slate-200">
        <h2 className="font-display text-2xl sm:text-3xl font-bold mb-3">Local insurance, across four states</h2>
        <p className="text-slate-600 max-w-3xl">Keystone Shield Insurance is based in Mount Pocono, Pennsylvania and proudly serves clients throughout <strong>Pennsylvania, New Jersey, New York, and Florida</strong>. Whether you're comparing car insurance in Monroe County, looking for a cheaper home insurance rate in the Poconos, or need business coverage nearby, we shop multiple carriers to help you find affordable coverage.</p>
        <div className="mt-6 flex flex-wrap gap-2">
          {["Mount Pocono, PA", "Stroudsburg, PA", "East Stroudsburg, PA", "Tobyhanna, PA", "Monroe County, PA", "Scranton, PA", "Allentown, PA", "Pennsylvania", "New Jersey", "New York", "Florida"].map((a) => (
            <span key={a} className="text-sm bg-white border border-slate-200 rounded-sm px-3 py-1.5 text-slate-700">{a}</span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#C5A059]">
        <div className="max-w-7xl mx-auto px-5 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0B1E36]">Ready to see how much you could save?</h2>
          <Link to="/quote" className="bg-[#0B1E36] text-white font-bold px-8 h-14 rounded-sm flex items-center" data-testid="cta-get-quote">GET A FREE QUOTE</Link>
        </div>
      </section>
    </div>
  );
}
